// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import type { ComponentChildren } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import {
  ApiError,
  api,
  isOfferUnavailable,
  type OfferResponse,
  type OfferResult,
  type TermsStatusResponse,
  type VaultSummary,
} from '../api.js';
import { useSession, offerGateSignal } from '../session-context.js';
import { TermsModal } from '../components/terms-modal.js';
import { ResidencyModal } from '../components/residency-modal.js';
// COB-549: the reassurance sentence is imported, never re-typed — it lives
// in one place on purpose (see its doc comment in geo-restricted.tsx).
import { GEO_SERVICING_NOTE } from '../components/geo-restricted.js';
import { resolveBrand } from '../brand.js';
import { BrandPill } from '../components/brand-lockup.js';
import { productTypeLabel } from '../components/product-subtitle.js';
import {
  formatBtc,
  formatCountdownShort,
  formatMaturityShort,
  formatPercent,
  formatUsd,
  periodRewardFraction,
  truncate,
} from '../format.js';
import { Onboarding } from './onboarding.js';
import { HardwareLogin } from '../components/hardware-login.js';
import { Web3AuthEmailCapture } from '../components/web3auth-email-capture.js';
import { Web3AuthLossAck } from '../components/web3auth-loss-ack.js';
import {
  LoginMethodChooser,
  useWeb3AuthLogin,
} from '../components/login-method-chooser.js';
import '../styles/home.css';

/** F17.3: gates the offer-card "Accept offer" button + the wallet
 *  page on KYC pass. The Layout banner explains the gate; this helper
 *  is the FE-side authority on whether a user can transact. The
 *  backend enforces it independently at `POST /vault`. */
export function canAcceptOffers(
  isAuthenticated: boolean,
  kycStatus: string | null,
): boolean {
  return isAuthenticated && kycStatus === 'passed';
}

/** COB-260: advisory gate on the "Open this vault" button — the user must
 *  also have accepted the current ProductCo T&C version, on top of clearing
 *  KYC. Purely advisory: `POST /vault` independently re-checks both the KYC
 *  status and the accepted terms version server-side (create_vault gates
 *  0a/0b), so a user who bypasses this button gains nothing — the button just
 *  routes them through the terms modal first when they haven't accepted. */
export function canOpenVault(
  isAuthenticated: boolean,
  kycStatus: string | null,
  termsAccepted: boolean,
): boolean {
  return canAcceptOffers(isAuthenticated, kycStatus) && termsAccepted;
}

/** Terminal vault states — excluded from the default home-page list so
 *  users aren't forced to scroll past dozens of failed / settled vaults
 *  every time they log in. A toggle reveals them on demand. */
export const INACTIVE_VAULT_STATES: ReadonlySet<string> = new Set([
  'CLOSED',
  'ABANDONED',
  'FAILED',
]);

export function isInactiveVault(v: Pick<VaultSummary, 'state'>): boolean {
  return INACTIVE_VAULT_STATES.has(v.state);
}

/** Pure sort helper — newest first by `created_at_unix`. Exposed for
 *  unit tests; the component wraps this in a useMemo. */
export function sortVaultsNewestFirst(vaults: VaultSummary[]): VaultSummary[] {
  return [...vaults].sort((a, b) => b.created_at_unix - a.created_at_unix);
}

/** States whose full contracted principal has actually been deposited and
 *  locked in the vault — i.e. the deposit cleared the funding gate. These
 *  are the only states where summing `principal_sats` (the *contracted*
 *  target, not the live on-chain balance) is a true "deposited" figure:
 *    - ACCEPTED  → contract accepted, nothing sent yet — NOT deposited.
 *    - FUNDING   → a deposit is on-chain but still confirming and may be
 *                  partial, so `principal_sats` would over-state it — NOT
 *                  counted until it clears to FUNDED.
 *  FUNDED/ACTIVE/SETTLING/EXPIRED/SETTLED have all passed the gate, so the
 *  contracted principal is in the vault. Terminal states are excluded
 *  upstream via `isInactiveVault`. (COB-261 follow-up: the earlier version
 *  summed over every held vault, so a not-yet-funded ACCEPTED vault inflated
 *  "Total deposited".) */
const DEPOSITED_VAULT_STATES: ReadonlySet<string> = new Set([
  'FUNDED',
  'ACTIVE',
  'SETTLING',
  'EXPIRED',
  'SETTLED',
]);

/** COB-637: "this vault's deposit has cleared the funding gate" — the single
 *  predicate behind every deposited figure in the app. Exported because the
 *  browser-wipe warning has to ask the same question over the HELD set (a
 *  vault whose revised terms are still unsigned nonetheless holds its BTC),
 *  and a second copy of this state list is the next thing that would drift. */
export function hasClearedFundingGate(v: Pick<VaultSummary, 'state'>): boolean {
  return DEPOSITED_VAULT_STATES.has(v.state);
}

/** COB-349 follow-up: a FUNDED vault whose agreed offer was invalidated and
 *  is awaiting the user's re-acceptance of a revised offer
 *  (`sub_status = 'offer_invalid'`). Its contracted principal/reward reflect
 *  terms the user has NOT re-agreed, so it must not count toward the
 *  "Total deposited" / "Currently earning" portfolio figures until re-approved.
 *  Once the user re-accepts, `sub_status` flips to 'offer_reapproved' and the
 *  backend has ALREADY replaced the contract with the newly-agreed terms
 *  (`update_vault_contract`), so those figures are legitimate again — an
 *  'offer_reapproved' vault is deliberately NOT excluded. */
export function isAwaitingReaccept(
  v: Pick<VaultSummary, 'state' | 'sub_status'>,
): boolean {
  return v.state === 'FUNDED' && v.sub_status === 'offer_invalid';
}

/** COB-429 / COB-428: a vault whose owner has NOT confirmed the currently-
 *  registered contract with the independent verifier — either because a
 *  reaccept replaced it (`reapproved_awaiting_ack`) or because the reward
 *  landed on a contract that was never acknowledged (`awaiting_ack`). Nothing proceeds
 *  until they confirm, so the card carries the same attention highlight as
 *  `offer_invalid`. Deliberately NOT an exclusion from the portfolio sums: the
 *  economic terms ARE agreed (the user re-accepted the offer, and the backend
 *  has already replaced the contract with them) — only the independent
 *  acknowledgement is outstanding, exactly as for `offer_reapproved`. */
export function isAwaitingContractAck(
  v: Pick<VaultSummary, 'sub_status'>,
): boolean {
  return (
    v.sub_status === 'reapproved_awaiting_ack' || v.sub_status === 'awaiting_ack'
  );
}

export interface VaultPortfolioTotals {
  /** Count of held (non-inactive) vaults. */
  heldCount: number;
  /** COB-349 follow-up: count of vaults contributing to `totalDepositedSats` /
   *  `earningSats` — those past the funding gate (`DEPOSITED_VAULT_STATES`) and
   *  NOT awaiting offer re-acceptance (`isAwaitingReaccept`). The portfolio
   *  tile's eyebrow reads this instead of `heldCount` so the "across N vaults"
   *  copy references exactly the set the tile actually sums. */
  depositedCount: number;
  /** Sum of contracted principal across vaults whose deposit has cleared the
   *  funding gate (`DEPOSITED_VAULT_STATES`); excludes ACCEPTED/FUNDING. */
  totalDepositedSats: number;
  /** COB-349: sum of contracted `reward_sats` (sats) across the SAME deposited
   *  set as `totalDepositedSats` — the yield the deposited capital earns, not
   *  the deposit itself. This deliberately includes SETTLING/EXPIRED/SETTLED,
   *  whose contracted reward no longer accrues, so the "Currently earning" and
   *  "Total deposited" tiles cover exactly the same vault set (terminal vaults
   *  excluded upstream via `isInactiveVault`). */
  earningSats: number;
  /** COB-637: soonest future settlement across the SAME deposited set as
   *  `totalDepositedSats` / `earningSats`, or null if none. It is a tile
   *  figure, so it names the same vaults the two figures beside it sum and
   *  the eyebrow above them counts. */
  nextSettlementUnix: number | null;
}

/** COB-261: fold the already-fetched vault list into the portfolio-summary
 *  figures. This is a pure aggregation — every field is derived from
 *  `VaultSummary` rows the /vaults list has already loaded, so it adds no
 *  new data or endpoint (visual-only redesign). Terminal vaults (CLOSED /
 *  ABANDONED / FAILED) are excluded via `isInactiveVault`. Exposed for
 *  unit tests; the component wraps it in a useMemo.
 *
 *  COB-637 — the one rule for "your vaults", per surface, because two counts
 *  come out of this one result type: **the /vaults tile (eyebrow, both BTC
 *  figures AND the "Next settlement" countdown) reads the DEPOSITED set, so
 *  the whole card is a single statement about a single set of vaults; the
 *  browser-wipe modal reads the HELD set — its count AND its BTC figure
 *  (`WipeExposure.heldDepositedSats`, never this tile's `totalDepositedSats`)
 *  — because a wipe destroys this browser's key material for every
 *  non-terminal vault whether or not its deposit has cleared, and that
 *  warning must never undercount.** Before COB-637 the countdown scanned the
 *  held set, so `/vaults` could show "Next settlement · 3d 4h" for an
 *  ACCEPTED vault contributing 0.00000000 to both figures printed beside it,
 *  or for a reaccept-pending FUNDED vault whose settlement date COB-351 had
 *  deliberately removed from the card; and the wipe modal printed this tile's
 *  sum beside the held count, understating the BTC behind the keys it was
 *  about to destroy by the whole of a reaccept-pending deposit.
 *
 *  The accepted cost of the tile half of that rule: a FUNDING vault — a
 *  deposit that IS on-chain, confirming, possibly partial — is outside the
 *  deposited set on purpose (`principal_sats` would over-state it), so an
 *  account whose only vault is FUNDING now reads "across 0 vaults",
 *  0.00000000 twice, AND no countdown: the card is silent rather than
 *  half-true, and the vault's own row still carries its state and its date. */
export function summarizeVaults(
  vaults: VaultSummary[],
  nowSeconds: number = Math.floor(Date.now() / 1000),
): VaultPortfolioTotals {
  let heldCount = 0;
  let depositedCount = 0;
  let totalDepositedSats = 0;
  let earningSats = 0;
  let nextSettlementUnix: number | null = null;
  for (const v of vaults) {
    if (isInactiveVault(v)) continue;
    heldCount += 1;
    const principal = Math.max(0, v.principal_sats);
    // COB-349 follow-up: a reaccept-pending FUNDED vault (`offer_invalid`) is
    // held, but its figures reflect terms the user has not re-agreed, so it is
    // excluded from the deposited/earning sums AND their count. An
    // 'offer_reapproved' vault is NOT excluded — the backend has already
    // written the re-agreed contract, so its principal/reward are legitimate.
    if (hasClearedFundingGate(v) && !isAwaitingReaccept(v)) {
      depositedCount += 1;
      totalDepositedSats += principal;
      // COB-349: "Currently earning" = contracted reward over the deposited
      // set, not the deposit itself. Same set as `totalDepositedSats`.
      earningSats += Math.max(0, v.reward_sats);
      // COB-637: the countdown is the third figure on the same tile, so it
      // scans this deposited set — not the wider held set it used to scan.
      // A settlement date the user has not funded (ACCEPTED / FUNDING) or has
      // not re-agreed (`offer_invalid`) is not this card's next settlement.
      const settle = v.settlement_reference_time_unix;
      if (Number.isFinite(settle) && settle > nowSeconds) {
        if (nextSettlementUnix === null || settle < nextSettlementUnix) {
          nextSettlementUnix = settle;
        }
      }
    }
  }
  return { heldCount, depositedCount, totalDepositedSats, earningSats, nextSettlementUnix };
}

// ── Offer status card (COB-386) ───────────────────────────────────────
//
// The `/offer` widget can be in one of three non-offer states, each of
// which now renders as a small variant-driven card in place of the old
// bare red error line. `tone` selects the visual intent:
//   • neutral   — not a fault; a normal "no offer right now" market state
//   • transient — a temporary problem we auto-retry through
//   • error     — the caller must change something (or wait out a limit)
// "Contact support" only surfaces after repeated transient failures, never
// on the first occurrence.

/** How often a neutral/transient card silently re-fetches the offer. The
 *  approved copy promises an auto-refresh, so the timer must be real. */
export const OFFER_AUTO_RETRY_MS = 15_000;

/** Consecutive transient failures before the card offers a support link. */
export const OFFER_SUPPORT_AFTER_ATTEMPTS = 3;

export type OfferCardTone = 'neutral' | 'transient' | 'error';

export interface OfferStatusCard {
  tone: OfferCardTone;
  title: string;
  body: string;
  /** Render a manual "Retry" button. */
  retry: boolean;
  /** Re-fetch on the {@link OFFER_AUTO_RETRY_MS} timer while shown. */
  autoRetry: boolean;
  /** Offer a "contact support" affordance (repeated transient failure only). */
  support: boolean;
  /** COB-403: render the house "no offers" empty state instead of a
   *  status card — the steady-state marketplace view for "nothing on the
   *  shelf", per the design-reviewed render. Absent on every other card. */
  empty?: boolean;
  /** COB-549 (GF-4): drop the empty state's "Join the newsletter" CTA.
   *  Set on exactly one card — the region-restricted view shown to a user
   *  who HAS attested a residency we cannot serve. A waitlist signup is a
   *  solicitation, and GF-3's whole position is that soliciting is the one
   *  thing geography restricts; offering it to someone we have just told we
   *  cannot serve them would contradict the gate we are rendering. */
  suppressWaitlist?: boolean;
  /** COB-549: an extra call-to-action beside (or instead of) Retry. Today
   *  the only value re-opens the residency modal. */
  action?: 'confirm_residency';
}

/** COB-549 (GF-4): the "we don't serve where you live" copy, shown once the
 *  user has told us where that is. Exported and test-pinned for the same
 *  reason `GEO_SERVICING_NOTE` is — this is the sentence that must not drift
 *  into implying someone's Bitcoin is stranded. */
export const REGION_RESTRICTED_TITLE = "Offers aren't available in your region";
export const REGION_RESTRICTED_BODY = `We can't offer Cobault's yield strategies where you live. ${GEO_SERVICING_NOTE}`;

/** COB-549 (GF-4): the prompt shown when the gate said `region_restricted`
 *  but we have no attested residency to explain it — we may simply not know
 *  where the user lives. Test-pinned alongside its sibling above. */
export const REGION_CONFIRM_TITLE = 'Confirm where you live to see offers';
export const REGION_CONFIRM_BODY =
  'Yield strategies are offered in some countries and not others. Confirm your country of residence and we’ll show you what’s available to you.';

/**
 * COB-386: map the backend's 200 `{ available: false, reason }` state to a
 * neutral card. `reason` is a stable machine code from user-app's
 * `OFFER_UNAVAILABLE_*` constants; term/expiry is deliberately absent from
 * the copy (it is not user-facing). Unknown reasons fall back to the
 * generic "no offer right now" line rather than an outage message.
 *
 * COB-549 (GF-4): `residencyAttested` is the caller's local session state
 * (`Session.residencyAttested`) and matters for exactly one reason code —
 * `region_restricted`, which branches on it. `null`/absent means the status
 * probe has not answered; that is treated the same as "not attested",
 * because offering the residency prompt to someone who has already attested
 * is a small annoyance while withholding it from someone who has not would
 * leave them stuck at a wall with no door.
 */
export function offerUnavailableCard(
  reason: string,
  residencyAttested?: boolean | null,
): OfferStatusCard {
  switch (reason) {
    case 'maintenance':
      return {
        tone: 'neutral',
        title: 'Offers paused for maintenance',
        body: "Yield offers are paused briefly for maintenance. They'll be back shortly.",
        retry: true,
        autoRetry: true,
        support: false,
      };
    // COB-400: per-user offer gate. `not_available_to_account` (the account
    // is not yet admitted — not_whitelisted or any unknown gated disposition)
    // renders the SAME house empty state as an empty shelf (COB-402 ruling,
    // 2026-07-20, superseding COB-400's quiet teaser): a gated account is
    // deliberately indistinguishable from "nothing on the shelf", and the
    // waitlist CTA is the admission path. The one behavioural difference is
    // invisible: NO autoRetry — a gated account must not poll.
    case 'not_available_to_account':
      return {
        tone: 'neutral',
        empty: true,
        title:
          'Wealth strategies from licensed providers will appear here when available',
        body: 'Check back again, or join our newsletter to follow progress with occasional updates.',
        retry: false,
        autoRetry: false,
        support: false,
      };
    // COB-400: the gate itself could not decide (upstream/kyc-store outage) —
    // transient and retryable, so mirror the maintenance treatment: a neutral
    // card that retries (manual + auto), never the teaser.
    case 'gate_unavailable':
      return {
        tone: 'neutral',
        title: 'Offers momentarily unavailable',
        body: "We couldn't load offers just now. We'll keep trying automatically.",
        retry: true,
        autoRetry: true,
        support: false,
      };
    // COB-549 (GF-4): the gate refused on jurisdiction. Two very different
    // situations wear the same disposition, and the local attestation state
    // tells them apart:
    //
    //  • not attested (or unknown) — we may simply not know where this user
    //    lives. That is a question, not a refusal, so the card asks it and
    //    offers the modal. No autoRetry: nothing changes until the user
    //    answers, and polling a gate that has already decided is noise.
    //  • attested — the user told us, and the answer is that we do not
    //    serve there. That is final, so the card says so plainly in the
    //    voice of the GF-3 region screen, carries no retry of any kind, and
    //    suppresses the waitlist CTA (see `suppressWaitlist`).
    //
    // Neither arm touches servicing: both are rendered to a fully
    // authenticated user whose existing vaults remain viewable, signable,
    // and withdrawable.
    case 'region_restricted':
      return residencyAttested === true
        ? {
            tone: 'neutral',
            empty: true,
            suppressWaitlist: true,
            title: REGION_RESTRICTED_TITLE,
            body: REGION_RESTRICTED_BODY,
            retry: false,
            autoRetry: false,
            support: false,
          }
        : {
            tone: 'neutral',
            title: REGION_CONFIRM_TITLE,
            body: REGION_CONFIRM_BODY,
            action: 'confirm_residency',
            retry: false,
            autoRetry: false,
            support: false,
          };
    case 'no_viable_spread':
    default:
      // COB-403: the steady-state "no offers on the shelf" view is the
      // house empty state, not a retry card — no retry affordance, but
      // autoRetry stays true so a returning offer swaps straight in.
      return {
        tone: 'neutral',
        empty: true,
        title:
          'Wealth strategies from licensed providers will appear here when available',
        body: 'Check back again, or join our newsletter to follow progress with occasional updates.',
        retry: false,
        autoRetry: true,
        support: false,
      };
  }
}

// COB-476/COB-486 §5: `offerGateSignal` (the `/offer`-outcome → gate-signal
// mapping) moved to session-context.tsx, which now resolves the gate itself
// on nav mount and cannot import a page module. Re-exported from here so this
// page's original consumers keep importing it from where it has always been.
export { offerGateSignal };

/**
 * COB-386: map a thrown `/offer` failure to a status card. `attempt` is the
 * count of consecutive transient failures *including* this one — used only
 * to decide whether to surface the support affordance. Tone/title/body are
 * a pure function of the failure kind, never of the raw server message
 * (which is log-only and may name internal policy bounds).
 */
export function offerErrorCard(e: unknown, attempt: number): OfferStatusCard {
  // A thrown non-ApiError means `fetch` itself failed — the browser is
  // offline or the network dropped before any HTTP status came back.
  if (!(e instanceof ApiError)) {
    return {
      tone: 'transient',
      title: 'You appear to be offline',
      body: "You appear to be offline. Check your connection and we'll retry.",
      retry: true,
      autoRetry: true,
      support: attempt >= OFFER_SUPPORT_AFTER_ATTEMPTS,
    };
  }
  switch (e.status) {
    case 429:
      // Rate-limited: retrying immediately only makes it worse, so no
      // auto-retry and no manual button — just wait it out.
      return {
        tone: 'error',
        title: 'Slow down a moment',
        body: "You're refreshing a little too fast — give it a few seconds.",
        retry: false,
        autoRetry: false,
        support: false,
      };
    case 400:
      // Guardrail rejected trading's offer. Rare; the raw reason is
      // log-only. Manual retry, no auto-loop, no "it's our fault" framing.
      return {
        tone: 'error',
        title: "Couldn't confirm an offer",
        body: 'We couldn’t confirm a valid offer just now. Please try again shortly.',
        retry: true,
        autoRetry: false,
        support: false,
      };
    case 503:
      // Defensive: maintenance normally arrives as a 200 `maintenance`
      // reason, but treat an error-path 503 the same way for symmetry.
      return offerUnavailableCard('maintenance');
    case 500:
      return {
        tone: 'transient',
        title: 'Something went wrong',
        body: 'Something went wrong on our end. Retrying…',
        retry: true,
        autoRetry: true,
        support: attempt >= OFFER_SUPPORT_AFTER_ATTEMPTS,
      };
    default:
      // 502/504 and any other upstream failure: trading is unreachable.
      return {
        tone: 'transient',
        title: "Can't reach pricing",
        body: "We can't reach the pricing engine right now. Retrying automatically…",
        retry: true,
        autoRetry: true,
        support: attempt >= OFFER_SUPPORT_AFTER_ATTEMPTS,
      };
  }
}

export function Home() {
  const session = useSession();
  // COB-476: destructured because `loadOffer` depends on it — the callback
  // is referentially stable, unlike the `session` object itself, so the
  // offer-fetch effect doesn't re-fire on every session re-render.
  // COB-549: `residencyAttested` is destructured for the same reason — it is
  // a card-mapping input, and re-fetching the offer when it changes is
  // deliberate: a just-recorded attestation may unlock an offer.
  const { noteOfferGated, residencyAttested } = session;
  const { route } = useLocation();
  const [offer, setOffer] = useState<OfferResponse | null>(null);
  // COB-386: the non-offer state (neutral / transient / error card), or
  // null when a live offer is shown or the first fetch is still in flight.
  const [offerStatus, setOfferStatus] = useState<OfferStatusCard | null>(null);
  // Consecutive transient failures, so the card can escalate to a support
  // affordance only after it has genuinely failed a few times in a row.
  const transientAttemptsRef = useRef(0);
  // COB-260: ProductCo T&C acceptance state for the create-flow gate.
  const [termsStatus, setTermsStatus] = useState<TermsStatusResponse | null>(null);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  // COB-549 (GF-4) / COB-582: the residency prompt. `residencyModalForced`
  // is the user asking for it via the offer card's CTA — since COB-582 the
  // only thing that opens the modal at all.
  const [residencyModalForced, setResidencyModalForced] = useState(false);
  // F14.14 / COB-220: explicit login-method chooser before the onboarding
  // flow, shared with the shared-vault accept landing. The hook owns the
  // Web3Auth handler + the one-account-per-email collision bounce.
  const {
    loginMethod,
    setLoginMethod,
    web3authBusy,
    web3authError,
    collisionEmail,
    setCollisionEmail,
    handleWeb3Auth,
  } = useWeb3AuthLogin();

  // F17.3: offer fetch is now gated only on authentication, not KYC.
  // Pre-KYC users browse the offer card to understand what they're
  // verifying for; the layout banner directs them to complete KYC,
  // and the offer-card Accept button is disabled until they do. The
  // /vault endpoint independently enforces KYC server-side, so a
  // disabled button can't be bypassed via direct API hit.
  const offerVisible = session.isAuthenticated;
  const acceptEnabled = canAcceptOffers(session.isAuthenticated, session.kycStatus);
  const termsAccepted = termsStatus?.accepted ?? false;

  // COB-549 (GF-4) / COB-582: whether the residency modal is on screen.
  //
  // CTA-only. The modal opens when — and ONLY when — the user has asked for
  // it by clicking "Confirm your residency" on the `region_restricted` offer
  // card. COB-549 also auto-opened it for every authenticated user with no
  // attestation on file; COB-582 removed that, because it put a 249-entry
  // country picker in front of people who had not asked a question it
  // answers. The card is the right place to ask: the user has just been told
  // why they cannot see offers, so the question has a visible reason.
  //
  // `!termsModalOpen` survives the change: the two prompts sequence, they
  // never stack, and terms is the one with a hard server gate behind it so
  // it goes first. `isAuthenticated` is belt-and-braces — the offer card
  // that carries the CTA only renders to a signed-in user, but the modal
  // posts to an authenticated endpoint and should not open without one.
  const residencyModalOpen =
    !termsModalOpen && session.isAuthenticated && residencyModalForced;

  // COB-386: fetch the offer and resolve to exactly one of three states —
  // a live offer, a neutral "no offer right now" card, or an error/transient
  // card. Distinct from the old fire-and-forget fetch so the Retry button
  // and the auto-refresh timer can both re-run it.
  const loadOffer = useCallback(async () => {
    try {
      const res: OfferResult = await api.getOffer();
      transientAttemptsRef.current = 0;
      // COB-476: definitive outcomes update the session's offer-gate
      // observation (Layout suppresses the KYC banner while gated).
      const gate = offerGateSignal(res);
      if (gate !== null) noteOfferGated(gate);
      if (isOfferUnavailable(res)) {
        setOffer(null);
        // COB-549: `region_restricted` renders differently depending on
        // whether we hold an attestation, so the card mapper gets the
        // session's view of that. Every other reason ignores it.
        setOfferStatus(offerUnavailableCard(res.reason, residencyAttested));
      } else {
        setOffer(res);
        setOfferStatus(null);
      }
    } catch (e: unknown) {
      const nextAttempt = transientAttemptsRef.current + 1;
      const card = offerErrorCard(e, nextAttempt);
      // Persist the streak only while we stay in a transient state, so a
      // non-transient failure (or a recovery) resets the support escalation.
      transientAttemptsRef.current = card.tone === 'transient' ? nextAttempt : 0;
      setOffer(null);
      setOfferStatus(card);
    }
  }, [noteOfferGated, residencyAttested]);

  useEffect(() => {
    if (!offerVisible) {
      setOffer(null);
      setOfferStatus(null);
      transientAttemptsRef.current = 0;
      return;
    }
    void loadOffer();
  }, [offerVisible, loadOffer]);

  // COB-386: honour the "auto-refreshes" / "retrying automatically" promise
  // in the card copy with a real timer. Only neutral + transient cards
  // auto-retry; error cards (rate-limit, guardrail) do not, so we never
  // hammer a limit. The timer re-arms on each new card object.
  useEffect(() => {
    if (!offerVisible || !offerStatus || !offerStatus.autoRetry) return;
    const id = window.setTimeout(() => {
      void loadOffer();
    }, OFFER_AUTO_RETRY_MS);
    return () => window.clearTimeout(id);
  }, [offerVisible, offerStatus, loadOffer]);

  // COB-260: once a user can accept offers (auth + KYC passed), load their
  // ProductCo T&C acceptance status so the "Open this vault" button knows
  // whether to route straight to /accept or present the terms modal first.
  // Fail-open to "not accepted" on a terms-surface outage — this is purely
  // advisory; POST /vault re-checks the accepted version server-side.
  useEffect(() => {
    if (!acceptEnabled) {
      setTermsStatus(null);
      return;
    }
    let cancelled = false;
    api
      .getTermsStatus()
      .then((t) => {
        if (!cancelled) setTermsStatus(t);
      })
      .catch(() => {
        if (!cancelled) setTermsStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [acceptEnabled]);

  // The "Open this vault" click when KYC-enabled: go straight to the accept
  // flow if the terms are already accepted, otherwise present the terms modal
  // first (accepting there continues to /accept).
  function handleOpenVault() {
    if (termsAccepted) route('/accept');
    else setTermsModalOpen(true);
  }

  return (
    <div>
      <div class="home-hero">
        {/* COB-376: mode-dependent hero title. Unauthenticated visitors see
            the keys-ownership line (the yield "ad banner" headline is not
            needed on the login view); authenticated users get the public-hero
            "put your Bitcoin to work" framing above their dashboard. The
            subtitle is unchanged in both modes. */}
        <h1>
          {session.isAuthenticated
            ? 'Put your Bitcoin to work'
            : 'Your keys. Your choice.'}
        </h1>
        <p>
          Your BTC never leaves the Bitcoin chain. No wrapping, no
          bridging, no transfer out of your control.
        </p>
      </div>

      {/* F14.14: method chooser → F12 onboarding flow. */}
      {!session.isAuthenticated && loginMethod === null && (
        <LoginMethodChooser
          onSelect={setLoginMethod}
          onWeb3Auth={handleWeb3Auth}
          web3authBusy={web3authBusy}
          web3authError={web3authError}
        />
      )}
      {!session.isAuthenticated && loginMethod === 'local' && (
        <>
          {collisionEmail && (
            <p class="login-collision-notice">
              You already have a Cobault account with{' '}
              <strong>{collisionEmail}</strong>. Sign in with the method you
              used when you created it — if that was a passcode, enter it below.
            </p>
          )}
          <Onboarding
            onBack={() => {
              setLoginMethod(null);
              setCollisionEmail(null);
            }}
            initialEmail={collisionEmail ?? undefined}
          />
        </>
      )}
      {/* HW.3: hardware (Ledger) sign-in panel. */}
      {!session.isAuthenticated && loginMethod === 'hardware' && (
        <HardwareLogin onBack={() => setLoginMethod(null)} />
      )}
      {/* COB-345: email capture for a provider-less Web3Auth sign-in. */}
      {!session.isAuthenticated && loginMethod === 'web3auth-email' && (
        <Web3AuthEmailCapture onBack={() => setLoginMethod(null)} />
      )}
      {/* COB-272: login-loss acknowledgement gating a new provider-email
          Web3Auth account's deferred register. A collision bounces to Self
          Custody with the email prefilled, mirroring the chooser. */}
      {!session.isAuthenticated && loginMethod === 'web3auth-ack' && (
        <Web3AuthLossAck
          onBack={() => setLoginMethod(null)}
          onCollision={(email) => {
            setCollisionEmail(email);
            setLoginMethod('local');
          }}
        />
      )}

      {/* COB-376: authed dashboard central pair — a "Shared vaults" card
          and a "Strategies" card wrapping the live offers section, side by
          side (stacking to one column on narrow viewports), mirroring the
          public home page's two-path layout re-skinned to the dark tokens.
          The offers section keeps its behaviour verbatim (F17.2 offer above
          vaults; F17.3 visible regardless of KYC with the Accept button
          gated below; COB-260 T&C modal on the accept path); only its
          placement moves inside the Strategies path card. COB-454: Shared
          vaults renders first (left on desktop, top on mobile) — the grid
          has no `order`/`nth-child` overrides, so visual order follows this
          DOM order verbatim. */}
      {offerVisible && (
        <div class="home-paths">
          <SharedVaultsPathCard />
          <section class="home-path-card">
            <span class="home-path-kicker">Strategies</span>
            <OffersSection
              offer={offer}
              status={offerStatus}
              acceptEnabled={acceptEnabled}
              termsAccepted={termsAccepted}
              onOpenVault={handleOpenVault}
              onRetry={() => void loadOffer()}
              onConfirmResidency={() => setResidencyModalForced(true)}
            />
          </section>
        </div>
      )}

      {/* COB-260: ProductCo Terms & Conditions acceptance modal. Opened from
          the offer card when a KYC-passed user has not accepted the current
          terms version; on accept it continues to /accept. */}
      {termsModalOpen && (
        <TermsModal
          onClose={() => setTermsModalOpen(false)}
          onAccepted={() => {
            setTermsModalOpen(false);
            route('/accept');
          }}
        />
      )}

      {/* COB-549 (GF-4) / COB-582: the residency prompt, opened only by the
          offer card's CTA. Dismissible by design — it is a prompt, not a
          gate (the gate is server-side, at productco-kyc), and a modal an
          authenticated user could not get past would sit between them and
          their own vaults. Dismissing simply closes it: there is no longer
          an auto-prompt for a remembered "Not now" to suppress, and the CTA
          is always there to re-open it. */}
      {residencyModalOpen && (
        <ResidencyModal
          initialJurisdiction={session.residencyJurisdiction}
          onDismiss={() => setResidencyModalForced(false)}
          onAttested={(jurisdiction) => {
            setResidencyModalForced(false);
            session.noteResidencyAttested(jurisdiction);
          }}
        />
      )}

      {/* F17.2: vault list now lives below the offer; full version
          (with header + show-inactive toggle) on Home dashboard, and
          dedicated /vaults route reuses the same component. */}
      {session.isAuthenticated && <VaultListSection />}
    </div>
  );
}

// ── Vault list section (F17.2) ─────────────────────────────────────────
//
// Extracted so that both Home and the new dedicated /vaults route render
// the same component. Home uses the section as a dashboard tile — with
// no vaults it renders nothing at all, since the offer panel above
// already carries the "nothing here yet" story. /vaults supplies its own
// empty state because that is the whole reason the user navigated there.

interface VaultListSectionProps {
  /** Copy shown when the user has no vaults. Home passes nothing and
   *  renders blank; /vaults supplies its own forward-looking copy. */
  emptyMessage?: ComponentChildren;
  /** COB-261: /vaults renders a dark portfolio-summary hero above the
   *  list (TOTAL DEPOSITED / CURRENTLY EARNING / NEXT SETTLEMENT). Home
   *  leaves it off so the dashboard stays unchanged. */
  portfolioSummary?: boolean;
}

export function VaultListSection({
  emptyMessage,
  portfolioSummary = false,
}: VaultListSectionProps) {
  const session = useSession();
  const [serverVaults, setServerVaults] = useState<VaultSummary[]>([]);
  useEffect(() => {
    if (!session.isAuthenticated) {
      setServerVaults([]);
      return;
    }
    api
      .listVaults()
      .then((r) => setServerVaults(r.vaults))
      .catch(() => {
        // Leave the list empty on fetch failure — do NOT fall back to
        // localStorage. A 401 will already have transitioned the
        // session to `expired` via the api.ts hook.
      });
  }, [session.isAuthenticated]);

  const sortedVaults = useMemo(
    () => sortVaultsNewestFirst(serverVaults),
    [serverVaults],
  );
  const inactiveCount = useMemo(
    () => sortedVaults.filter(isInactiveVault).length,
    [sortedVaults],
  );
  const [showInactive, setShowInactive] = useState(false);
  const visibleVaults = showInactive
    ? sortedVaults
    : sortedVaults.filter((v) => !isInactiveVault(v));

  if (sortedVaults.length === 0) {
    if (!emptyMessage) return null;
    return <div class="vault-list-empty">{emptyMessage}</div>;
  }

  return (
    <>
      {portfolioSummary && <VaultPortfolioSummary vaults={sortedVaults} />}
      <div class="vault-list">
        <div class="vault-list-header">
          <h2 class="vault-list-title">
            {/* COB-486 §5: the `portfolioSummary` branch renders only on
                /vaults, which is now the Strategies surface. Home's own
                heading (and every "vault" noun below it) is untouched —
                vault stays the custody object. */}
            {portfolioSummary ? 'Strategies' : 'Your active deposits'}
          </h2>
          {inactiveCount > 0 && (
            <button
              type="button"
              class="vault-list-filter"
              onClick={() => setShowInactive((v) => !v)}
            >
              {showInactive ? 'Hide inactive' : `Show all (${inactiveCount} inactive)`}
            </button>
          )}
        </div>
        {visibleVaults.map((v) => (
          <VaultCard key={v.vault_id} vault={v} />
        ))}
        {visibleVaults.length === 0 && (
          <p class="vault-list-empty-inline">
            All your deposits are inactive. Use the toggle above to view them.
          </p>
        )}
      </div>
    </>
  );
}

/** COB-261: portfolio-summary hero on /vaults. A dark `--surface` card
 *  carrying the three headline figures in the Fraunces display face,
 *  computed client-side from the already-loaded vault list (no new
 *  data). Rendered only when the list is non-empty. */
function VaultPortfolioSummary({ vaults }: { vaults: VaultSummary[] }) {
  const totals = useMemo(() => summarizeVaults(vaults), [vaults]);
  const nowSeconds = Math.floor(Date.now() / 1000);
  const nextSettlement =
    totals.nextSettlementUnix === null
      ? '—'
      : formatCountdownShort(totals.nextSettlementUnix - nowSeconds);
  return (
    <section class="vault-portfolio" aria-label="Portfolio summary">
      <div class="vault-portfolio-head">
        <span class="vault-portfolio-eyebrow">
          {/* COB-349 follow-up: count the vaults the tile actually sums
              (`depositedCount`), not raw held breadth, so "across N vaults"
              never claims a vault whose principal/reward is excluded below
              (e.g. a reaccept-pending FUNDED vault). */}
          Portfolio · across {totals.depositedCount}{' '}
          {totals.depositedCount === 1 ? 'vault' : 'vaults'}
        </span>
        <span class="vault-portfolio-chain">
          <span class="vault-portfolio-dot" aria-hidden="true" />
          On chain
        </span>
      </div>
      <div class="vault-portfolio-grid">
        <div class="vault-portfolio-stat">
          <div class="vault-portfolio-label">Total deposited</div>
          <div class="vault-portfolio-figure tabular-nums">
            {formatBtc(totals.totalDepositedSats)}
          </div>
          <div class="vault-portfolio-unit">BTC</div>
        </div>
        <div class="vault-portfolio-stat">
          <div class="vault-portfolio-label">Currently earning</div>
          <div class="vault-portfolio-figure vault-portfolio-figure--accent tabular-nums">
            {formatBtc(totals.earningSats)}
          </div>
          <div class="vault-portfolio-unit">BTC</div>
        </div>
        <div class="vault-portfolio-stat">
          <div class="vault-portfolio-label">Next settlement</div>
          <div class="vault-portfolio-figure">{nextSettlement}</div>
          <div class="vault-portfolio-unit">
            {totals.nextSettlementUnix === null ? 'none scheduled' : 'from now'}
          </div>
        </div>
      </div>
      <div class="vault-portfolio-foot">
        <span class="vault-portfolio-diamonds" aria-hidden="true">◇◆◇</span>
        Every vault is a 2-of-3 multisig · you hold one key
      </div>
    </section>
  );
}

// ── Vault state-line (Phase 3) ────────────────────────────────────────
//
// Today's VaultCard rendered the lifecycle state as a coloured pill
// with the raw enum (ACCEPTED, ACTIVE…) and an optional sub-status line
// underneath. Phase 3's status-as-context variant collapses both into a
// single human sentence and demotes the badge to a small adornment.
//
// The helper is pure so it can be unit-tested with injected `nowSeconds`.

/** Sub-status verb for the FUNDED yield cycle and the refund pipeline.
 *  The human half never names the mechanism (venue, "trade", etc.); the
 *  raw enum follows in parens so the protocol step is visible without
 *  inspecting the network tab. "Earning yield" is reserved for ACTIVE
 *  (see `vaultStateLine`), so it never fires before the reward lands.
 *  Returns `null` for sub-statuses with no verb of their own — the caller
 *  then keeps its per-state default. */
export function subStatusLabel(sub?: string): string | null {
  switch (sub) {
    case 'executing_trade':
      return 'Generating yield (executing_trade)';
    case 'trade_pending':
      return 'Generating yield (trade_pending)';
    case 'trade_filled':
      return 'Broadcasting reward (trade_filled)';
    case 'reward_broadcast':
      return 'Reward sent (reward_broadcast)';
    case 'offer_invalid':
      return 'Action needed (offer_invalid)';
    case 'offer_reapproved':
      return 'Resuming (offer_reapproved)';
    // COB-429 / COB-428: the owner has to confirm the registered contract with
    // the independent verifier before the vault proceeds — after a reaccept
    // replaced it, or after the reward landed on a never-acknowledged contract.
    case 'reapproved_awaiting_ack':
      return 'Action needed (reapproved_awaiting_ack)';
    case 'awaiting_ack':
      return 'Action needed (awaiting_ack)';
    case 'refund_initiating':
      return 'Refunding (refund_initiating)';
    case 'refund_co_signing':
      return 'Refunding (refund_co_signing)';
    case 'refund_broadcast':
      return 'Refund sent (refund_broadcast)';
    case 'refund_confirmed':
      return 'Refunded (refund_confirmed)';
    default:
      return null;
  }
}

interface VaultStateLineInput {
  state: string;
  sub_status?: string;
  settlement_reference_time_unix: number;
  created_at_unix: number;
  /** Injected for tests; defaults to wall-clock. */
  nowSeconds?: number;
}

/** "X of N days" suffix for mid-lifecycle states. Returns "" when the
 *  total term is non-positive (defensive: a malformed contract row
 *  shouldn't render "0 of 0 days") or when either input is non-finite
 *  (pre-Phase-3 vault rows come back without settlement_reference_time_unix,
 *  so the TS type says number but runtime is null/undefined). */
function dayProgressSuffix(
  createdAt: number,
  settleAt: number,
  now: number,
): string {
  if (!Number.isFinite(createdAt) || !Number.isFinite(settleAt)) return '';
  const total = Math.round((settleAt - createdAt) / 86400);
  if (total <= 0) return '';
  const elapsed = Math.max(0, Math.min(total, Math.round((now - createdAt) / 86400)));
  return `${elapsed} of ${total} days`;
}

export function vaultStateLine(input: VaultStateLineInput): string {
  const now = input.nowSeconds ?? Math.floor(Date.now() / 1000);
  const progress = dayProgressSuffix(
    input.created_at_unix,
    input.settlement_reference_time_unix,
    now,
  );
  const verb = (fallback: string): string =>
    subStatusLabel(input.sub_status) ?? fallback;
  const withProgress = (s: string): string =>
    progress ? `${s} · ${progress}` : s;

  switch (input.state) {
    case 'ACCEPTED':
      return 'Awaiting deposit';
    case 'FUNDING':
      return 'Confirming deposit';
    case 'FUNDED':
      // FUNDED runs the yield cycle: a NULL sub_status keeps the default
      // below, everything else takes its verb from `subStatusLabel`.
      // "Earning yield" is reserved for ACTIVE below.
      return withProgress(verb('Generating yield'));
    case 'ACTIVE':
      // Reward confirmed, term running: always "Earning yield",
      // regardless of any residual sub_status.
      return withProgress('Earning yield');
    case 'SETTLING':
      return withProgress(verb('Settling'));
    case 'EXPIRED':
      return 'Ready to settle · action needed';
    case 'SETTLED':
      return 'Settled · reward sent';
    case 'CLOSED':
      return 'Closed';
    case 'ABANDONED':
      return 'Abandoned';
    case 'FAILED':
      return 'Failed';
    default:
      return input.state;
  }
}

// ── Vault card ────────────────────────────────────────────────────────

function VaultCard({ vault }: { vault: VaultSummary }) {
  const hasAttention =
    vault.sub_status === 'offer_invalid' || isAwaitingContractAck(vault);
  const line = vaultStateLine({
    state: vault.state,
    sub_status: vault.sub_status,
    settlement_reference_time_unix: vault.settlement_reference_time_unix,
    created_at_unix: vault.created_at_unix,
  });
  return (
    <a
      href={`/vault/${vault.vault_id}`}
      class={`vault-card${hasAttention ? ' vault-card--attention' : ''}`}
    >
      <div class="vault-card-header">
        <span
          class={`vault-state-pill vault-state-${vault.state.toLowerCase()}`}
          title={vault.state}
        >
          {vault.state === 'EXPIRED' ? 'ready to settle' : vault.state.toLowerCase()}
        </span>
        <span class="vault-card-id mono">{truncate(vault.vault_id, 12)}</span>
      </div>
      {vault.principal_sats > 0 && (
        <div class="vault-card-principal tabular-nums">
          {formatBtc(vault.principal_sats)} BTC
        </div>
      )}
      <div class="vault-card-state-line">{line}</div>
      {vault.error_message && (
        <div class="vault-card-error">{truncate(vault.error_message, 60)}</div>
      )}
    </a>
  );
}

// ── Offers section (F17.2, retail-refresh Phase 3) ─────────────────────

export function OffersSection({
  offer,
  status,
  acceptEnabled,
  termsAccepted,
  onOpenVault,
  onRetry,
  onConfirmResidency,
}: {
  offer: OfferResponse | null;
  status: OfferStatusCard | null;
  acceptEnabled: boolean;
  termsAccepted: boolean;
  onOpenVault: () => void;
  onRetry: () => void;
  /** COB-549 (GF-4): open the residency modal. Optional so the many test
   *  mounts and any future embedder that never renders a region card do not
   *  have to supply it; the CTA is simply not rendered without it. */
  onConfirmResidency?: () => void;
}) {
  return (
    <section class="offers-section">
      <h2 class="offers-section-title">Coming soon in allowed jurisdictions</h2>
      <p class="offers-section-helper">
        Strategies from licensed providers will appear here when available.
      </p>
      {/* COB-386: exactly one of these three renders — a status card (or
          the COB-403 empty state), the loading line, or the live offer.
          `status` and `offer` are never both set. */}
      {status &&
        (status.empty ? (
          <OffersEmptyView card={status} />
        ) : (
          <OfferStatusCardView
            card={status}
            onRetry={onRetry}
            onConfirmResidency={onConfirmResidency}
          />
        ))}
      {!status && !offer && <p class="offer-loading">Loading offers...</p>}
      {offer && (
        <OfferCard
          offer={offer}
          acceptEnabled={acceptEnabled}
          termsAccepted={termsAccepted}
          onOpenVault={onOpenVault}
        />
      )}
    </section>
  );
}

// ── Offer status card (COB-386) ───────────────────────────────────────
//
// Replaces the old bare `<p class="offer-error">` line. Reuses the
// offer-card footprint so the section height does not jump between the
// offer and the no-offer states. Tone drives colour + glyph; a manual
// Retry button and (for neutral cards) an auto-refresh hint make the
// approved copy's promises truthful.

const OFFER_STATUS_GLYPH: Record<OfferCardTone, string> = {
  neutral: 'ⓘ',
  transient: '↻',
  error: '!',
};

function OfferStatusCardView({
  card,
  onRetry,
  onConfirmResidency,
}: {
  card: OfferStatusCard;
  onRetry: () => void;
  onConfirmResidency?: () => void;
}) {
  return (
    <div
      class={`offer-card offer-status-card offer-status-${card.tone}`}
      role="status"
      aria-live="polite"
    >
      <span class="offer-status-glyph" aria-hidden="true">
        {OFFER_STATUS_GLYPH[card.tone]}
      </span>
      <h3 class="offer-status-title">{card.title}</h3>
      <p class="offer-status-text">{card.body}</p>
      <div class="offer-status-actions">
        {card.retry && (
          <button type="button" class="offer-status-retry" onClick={onRetry}>
            Retry
          </button>
        )}
        {/* COB-549 (GF-4): the residency prompt's way back in. Rendered only
            when the caller wired a handler — a card with nowhere to send the
            click is worse than no button. */}
        {card.action === 'confirm_residency' && onConfirmResidency && (
          <button
            type="button"
            class="offer-status-action"
            onClick={onConfirmResidency}
          >
            Confirm your residency
          </button>
        )}
        {card.autoRetry && card.tone === 'neutral' && (
          <span class="offer-status-hint">Auto-refreshes every 15s</span>
        )}
        {card.support && (
          <a class="offer-status-support" href="mailto:support@cobault.io">
            Contact support
          </a>
        )}
      </div>
    </div>
  );
}

// ── Offers empty state (COB-403) ──────────────────────────────────────

/** Destination for the empty state's waitlist CTA (COB-402/404): the real
 *  per-environment waitlist links. The bundle default is the PROD link so
 *  byte-identical SPA promotion behaves correctly on prod with no per-env
 *  build; the staging host is the runtime override — the same env-shim
 *  pattern as the public home page. Opens in a new tab: the session JWT is
 *  in-memory, so in-tab navigation away would sign the user out. */
const WAITLIST_URL =
  location.hostname === 'wrap.staging.cobault.io'
    ? 'https://go.cobault.net/stg_offer'
    : 'https://go.cobault.net/prod_offer';

/**
 * COB-403: the house-styled "no offers on the shelf" view, per the
 * design-reviewed render (critique project, fail-safe card "no offers"
 * lane). Stands in for the offer card when no productco supplies an
 * offer — deliberately on stock Cobault tokens (no offer means no
 * counterparty, so no brand scope) with a single house-gold primary CTA.
 * When the shelf is genuinely empty the silent {@link OFFER_AUTO_RETRY_MS}
 * refresh keeps running behind it so a returning offer swaps straight in;
 * the gated-account card (COB-402) renders this same view with autoRetry
 * off — indistinguishable on screen, but a gated account never polls.
 */
function OffersEmptyView({ card }: { card: OfferStatusCard }) {
  return (
    <div class="offers-empty" role="status" aria-live="polite">
      <span class="offers-empty-icon" aria-hidden="true">
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      </span>
      <div class="offers-empty-copy">
        <p class="offers-empty-title">{card.title}</p>
        <p class="offers-empty-body">{card.body}</p>
      </div>
      {/* COB-549 (GF-4): the waitlist CTA is a solicitation, and the one
          thing geography restricts is soliciting — so the region-restricted
          card suppresses it. Every other empty-state card keeps it. */}
      {!card.suppressWaitlist && (
        <div class="offers-empty-actions">
          <a
            class="btn btn-primary"
            href={WAITLIST_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            Join the newsletter
          </a>
        </div>
      )}
    </div>
  );
}

// ── Shared-vaults path card (COB-376) ─────────────────────────────────
//
// COB-454: the left-hand (first) path card on the authed dashboard,
// mirroring the public home page's "Shared vaults" path card re-skinned to
// the dark tokens. Static marketing content + two route links: the primary
// button opens the shared-vault create flow, the text link the user's
// existing shared vaults. Plain <a href> navigation, matching
// shared-vaults.tsx's create CTA idiom.
function SharedVaultsPathCard() {
  return (
    <section class="home-path-card">
      <span class="home-path-kicker">Shared vaults</span>
      <h3 class="home-path-title">Hold Bitcoin together, on your terms</h3>
      <p class="home-path-intro">
        Split keys across the people you need to collaborate with — a
        partner, your family, your business — and optionally add policies
        the vault enforces on-chain.
      </p>
      <div class="home-path-features">
        <div class="home-path-feature">
          <span class="home-path-feature-glyph" aria-hidden="true">
            <span class="home-path-triad">
              <i />
              <i class="you" />
              <i />
            </span>
          </span>
          <div>
            <h4>Peer to peer</h4>
            <p>Set your own n-of-m. Spends need agreement, no single point of failure.</p>
          </div>
        </div>
        <div class="home-path-feature">
          <span class="home-path-feature-glyph" aria-hidden="true">§</span>
          <div>
            <h4>Optionally policy enabled</h4>
            <p>Spending limits and approve-listed addresses enforced by the optional independent verifier.</p>
          </div>
        </div>
        <div class="home-path-feature">
          <span class="home-path-feature-glyph" aria-hidden="true">↻</span>
          <div>
            <h4>Built-in recovery</h4>
            <p>Lose a key and the remaining holders can restore access (depends on quorum settings).</p>
          </div>
        </div>
      </div>
      <div class="home-path-actions">
        <a class="home-path-btn" href="/shared-vaults/create">
          Create a shared vault
        </a>
        <a class="home-path-link" href="/shared-vaults">
          Your shared vaults
        </a>
      </div>
    </section>
  );
}

// ── Offer card (Phase 3 minimal deal-tile) ────────────────────────────

/** F17.2: per-1-BTC reward in BTC. The user's actual reward equals
 *  `principal_btc × periodRewardFraction(yield_apy, days)` — see
 *  `expectedRewardSats` in format.ts. We display the per-1-BTC value
 *  on the offer card so the user can read the BTC denomination
 *  directly without first picking a deposit amount. */
export function perBtcYield(offer: OfferResponse): number {
  return periodRewardFraction(offer.economics.yield_apy, offer.days_to_expiry);
}

function OfferCard({
  offer,
  acceptEnabled,
  termsAccepted,
  onOpenVault,
}: {
  offer: OfferResponse;
  acceptEnabled: boolean;
  termsAccepted: boolean;
  onOpenVault: () => void;
}) {
  const perBtcSats = Math.round(perBtcYield(offer) * 1e8);
  const settleShort = formatMaturityShort(offer.maturity_date);
  // COB-394: sibling-brand treatment for the offer's counterparty. Inert
  // today — `offer.productco` is absent so `brand.attr` is undefined (the
  // `data-brand` attribute is omitted) and the lockup renders nothing, so
  // this card's DOM is identical to pre-COB-394.
  //
  // COB-403: a card under a known counterparty's treatment drops the
  // Cobault mark — the brand pill owns the identity corner and the two
  // marks were reading side by side. Legacy and unknown-id cards (no
  // `data-brand` scope) keep the mark.
  const brand = resolveBrand(offer.productco);
  return (
    <div class="offer-card" data-brand={brand.attr}>
      {!brand.attr && (
        <img
          src="/cobault-mark.png"
          alt=""
          class="offer-card-mark"
          width={32}
          height={32}
        />
      )}
      {brand.displayName && (
        <>
          <BrandPill brand={brand} class="offer-card-brand-pill" />
          {/* COB-402: the issuer byline renders the descriptor's own
              endorsement line ("A Cobault company") rather than a
              bundle-side "Issued & managed by" template — counterparty
              copy stays server data, per the brand mechanism's principle.
              No lockup on the descriptor → no byline (fail-safe). */}
          {brand.lockup && <p class="offer-card-issuer">{brand.lockup}</p>}
        </>
      )}
      <div class="offer-headline">
        <div class="offer-apy tabular-nums">
          {formatPercent(offer.economics.yield_apy)}
        </div>
        <div class="offer-apy-label">APY</div>
      </div>
      <p class="offer-risk-line">
        Earn{' '}
        <span class="offer-risk-strong tabular-nums">
          {formatBtc(perBtcSats)} BTC
        </span>{' '}
        per BTC over {offer.days_to_expiry} days. Up to{' '}
        <span class="offer-risk-strong">
          {formatPercent(offer.economics.max_loss_pct, 0)}
        </span>{' '}
        capital at risk if BTC closes above{' '}
        <span class="offer-risk-strong tabular-nums">
          {formatUsd(offer.short_leg.strike)}
        </span>{' '}
        on {settleShort}.
      </p>
      <div class="offer-accept">
        {!acceptEnabled ? (
          // F17.3: pre-KYC users see the offer but can't accept.
          // Render a disabled button so the gate is visible inline,
          // not just via the layout banner. The banner's "Complete
          // verification" CTA stays the path to unlock.
          <button
            type="button"
            class="offer-accept-btn offer-accept-btn--disabled"
            disabled
            title="Complete verification to accept offers"
          >
            Complete verification to accept
          </button>
        ) : termsAccepted ? (
          // KYC-passed and terms already accepted — straight to the
          // accept flow. Kept as an anchor so open-in-new-tab works.
          <a href="/accept" class="offer-accept-btn">
            Open this vault
          </a>
        ) : (
          // COB-260: KYC-passed but the current T&C version not yet
          // accepted — present the terms modal first (it continues to
          // /accept on acceptance). Purely advisory; POST /vault
          // re-checks the accepted version server-side.
          <button
            type="button"
            class="offer-accept-btn"
            onClick={onOpenVault}
          >
            Open this vault
          </button>
        )}
      </div>
      <details class="offer-full-terms">
        <summary class="offer-full-terms-summary">Show full terms</summary>
        <div class="offer-full-terms-body">
          <div class="offer-card-title">{productTypeLabel(offer.productco)}</div>
          <p class="offer-term">
            {offer.days_to_expiry}-day term · Matures {offer.maturity_date}
          </p>
          <div class="offer-metrics">
            <div class="offer-metric">
              <div class="offer-metric-label">Per-term return</div>
              <div class="offer-metric-value tabular-nums">
                {formatPercent(
                  periodRewardFraction(offer.economics.yield_apy, offer.days_to_expiry),
                )}
              </div>
            </div>
            <div class="offer-metric">
              <div class="offer-metric-label">Receive (per BTC)</div>
              <div class="offer-metric-value tabular-nums">
                {formatBtc(perBtcSats)} BTC
              </div>
            </div>
            <div class="offer-metric">
              <div class="offer-metric-label">Loss protection</div>
              <div class="offer-metric-value tabular-nums">
                From {formatUsd(offer.long_leg.strike)} (max{' '}
                {formatPercent(offer.economics.max_loss_pct)})
              </div>
            </div>
            <div class="offer-metric">
              <div class="offer-metric-label">Price cap (at)</div>
              <div class="offer-metric-value tabular-nums">
                {formatUsd(offer.short_leg.strike)}
              </div>
            </div>
            <div class="offer-metric">
              <div class="offer-metric-label">BTC spot</div>
              <div class="offer-metric-value tabular-nums">
                {formatUsd(offer.spot)}
              </div>
            </div>
            <div class="offer-metric">
              <div class="offer-metric-label">Capacity</div>
              <div class="offer-metric-value tabular-nums">
                {offer.offer_limit_btc.toFixed(4)} BTC
              </div>
            </div>
          </div>
          <p class="offer-footnote">
            Your vault keys derive from your master seed — no additional
            backup needed.
          </p>
        </div>
      </details>
    </div>
  );
}
